import { upperFirst } from 'scule'
import { IconNames } from '~/enums/icons/whitelisted'
import type { Alert } from '~/types'
import { APP_ROUTES } from '~/constants'
import { ErrorCodes } from '~/types/gql/generated/graphql'
import type { ButtonProps } from '#ui/components/Button.vue'
import type { AlertProps } from '#ui/components/Alert.vue'
import { useSessionStore } from '#imports'

const baseActionArgs = {
  size: 'xs',
  disabled: false,
}

const closeButton: ButtonProps = {
  icon: IconNames.x_close,
  color: 'neutral',
  variant: 'link',
}

export function useAlertBuilder() {
  const { t } = useI18n()
  const { remove } = useToast()
  const runtimeConfig = useRuntimeConfig()
  const { isAdmin } = storeToRefs(useSessionStore())

  function primaryActions(actions: ButtonProps[]) {
    return actions.map((action, index) => {
      return {
        color: action.color || 'primary',
        variant: action.variant || index === 0 ? 'solid' : 'outline-solid',
        ...baseActionArgs,
        ...action,
      }
    }) as ButtonProps[]
  }

  function ghostActions(actions: ButtonProps[]) {
    return actions.map((action, index) => {
      return {
        color: action.color || index === 0 ? 'primary' : 'gray',
        variant: action.variant || 'solid',
        ...baseActionArgs,
        ...action,
      }
    }) as ButtonProps[]
  }

  function linkActions(actions: ButtonProps[]) {
    return actions.map((action) => {
      return {
        color: 'primary',
        variant: 'link',
        ...baseActionArgs,
        ...action,
      }
    }) as ButtonProps[]
  }

  function downloadAndCloseActions(url: string, toastId: string) {
    return linkActions([
      {
        label: t('generics.download'),
        to: url,
        target: '_blank',
      },
      {
        label: t('generics.close'),
        color: 'neutral',
        onClick: () => remove(toastId),
      },
    ])
  }

  function supportContact(action?: ButtonProps): ButtonProps {
    return {
      label: t('generics.contact-support'),
      to: `mailto:${runtimeConfig.public.BUSINESS_CONTACTS.support}`,
      color: 'neutral',
      variant: 'link',
      ...action,
    } as ButtonProps
  }

  function backHome(action?: ButtonProps): ButtonProps {
    return {
      label: t('generics.back-home'),
      to: APP_ROUTES.home,
      color: 'primary',
      variant: 'link',
      ...action,
    } as ButtonProps
  }

  function buildFromGqlError(err: unknown, canClose = false, support = true, notification: Partial<Alert> = {}) {
    const { message, code } = extractMetadataFromResponse(err)

    const _code = code === ErrorCodes.BusinessLogic ? 'Not allowed' : code
    const _message = code === ErrorCodes.Forbidden && !isAdmin.value ? 'Not allowed' : message
    const alert = {
      actions: [],
      ...error(notification as AlertProps, canClose, support),
      title: upperFirst(_code.toLowerCase().replaceAll('_', ' ')),
      description: _message,
      ...notification,
    }

    return alert as Alert
  }
  function success(notification: Partial<Alert> = {}, canClose = false): Alert {
    const title = notification.title || t('generics.success')

    return {
      id: title,
      variant: 'subtle',
      actions: [],
      color: 'success',
      icon: IconNames.check_circle,
      closeButton: canClose ? closeButton : null,
      ...notification,
      title,
    } as Alert
  }

  function error(notification: Partial<Alert> = {}, canClose = false, support = true): Alert {
    const title = notification.title || t('generics.errors.request.title')

    const alert = {
      id: notification.id || notification.title,
      variant: 'subtle',
      actions: [],
      color: 'error',
      icon: IconNames.x_circle,
      ...notification,
      closeButton: canClose ? closeButton : null,
      title,
    }

    if (support)
      alert.actions.push(supportContact() as never)

    return alert as Alert
  }

  function info(notification: Partial<Alert> = {}, canClose = false, support = false) {
    const alert = {
      id: notification.title,
      variant: 'subtle',
      actions: [],
      color: 'info',
      icon: IconNames.info_circle,
      closeButton: canClose ? closeButton : null,
      ...notification,
    }
    if (support)
      alert.actions.push(supportContact() as never)

    return alert as Alert
  }

  function warning(notification: Partial<Alert> = {}, canClose = false): Alert {
    return {
      id: notification.title,
      variant: 'subtle',
      icon: IconNames.alert_circle,
      color: 'warning',
      closeButton: canClose ? closeButton : null,
      actions: [],
      ...notification,
    } as Alert
  }

  // eslint-disable-next-line ts/no-unsafe-function-type
  function confirm(onConfirm: Function, onCancel?: Function, notification?: Partial<Alert>): Alert {
    const alert = {
      id: notification?.id || notification?.title || t('generics.confirm'),
      title: notification?.title || t('generics.confirm'),
      variant: 'subtle',
      icon: IconNames.alert_circle,
      color: 'info',
      duration: 0,
      actions: [
        {
          label: t('generics.confirm'),
          onClick: onConfirm,
          color: 'primary',
          variant: 'solid',
        },
      ] as ButtonProps[],
      description: ' ',
      ...notification,
    }

    if (onCancel) {
      alert.actions.push({
        label: t('generics.cancel'),
        onClick: onCancel,
        variant: 'subtle',
        color: 'neutral',
      })
    }
    else {
      alert.actions.push({
        label: t('generics.cancel'),
        onClick: () => remove(alert.id),
        variant: 'subtle',
        color: 'neutral',
      })
    }
    return alert
  }

  function requestSave(
    // eslint-disable-next-line ts/no-unsafe-function-type
    onSave: Function,
    // eslint-disable-next-line ts/no-unsafe-function-type
    onDontSave: Function,
    notification?: Partial<Alert>,
    // eslint-disable-next-line ts/no-unsafe-function-type
    onCancel?: Function,
  ): Alert {
    const alert = {
      id: notification?.title || t('generics.request-save'),
      variant: 'subtle',
      icon: IconNames.alert_circle,
      color: 'info' as AlertProps['color'],
      duration: 0,
      actions: [
        {
          label: t('generics.confirm'),
          onClick: onSave,
          color: 'primary',
          variant: 'solid',
        },
      ] as ButtonProps[],
      description: ' ',
      ...notification,
    }

    if (onDontSave) {
      alert.actions.push({
        label: t('generics.exit-without-saving'),
        onClick: onDontSave,
        variant: 'outline',
        color: 'neutral',
      })

      alert.actions.push({
        label: t('generics.cancel'),
        onClick: onCancel || (() => remove(alert.id)),
        variant: 'subtle',
        color: 'neutral',
      })
    }

    alert.actions[0] = {
      ...alert.actions[0],
      label: t('generics.save'),
    }
    return alert
  }

  return {
    downloadAndCloseActions,
    supportContact,
    backHome,
    primaryActions,
    ghostActions,
    linkActions,
    buildFromGqlError,
    success,
    error,
    info,
    warning,
    confirm,
    requestSave,
  }
}
